<script lang="ts">
  import QRCode from '@/components/QR.svelte'
  import PageTopupSuccess from '@/components/appDriver/topup/PageTopupSuccess.svelte'
  import { topupPaymentGatewayResult, topupResult } from '@/components/appDriver/store'
  import { axiosInstance } from '@/plugins/axios'
  import { i18n, showErrorMessage } from '@/plugins/util'
  import { isLoading, lang } from '@/store'
  import axios from 'axios'
  import PubNub from 'pubnub'
  import PubSub from 'pubsub-js'
  import { onDestroy, onMount } from 'svelte'

  type PaymentMessage = Record<string, unknown>

  let pubnub: PubNub | undefined
  let pubnubListener: PubNub.Listener | undefined
  let pubnubChannels: string[] = []

  onMount(() => {
    pubnubChannels = $topupPaymentGatewayResult?.pubnubChannels ?? []
    initializePubNub()
    pubnubSubscribe()
  })

  onDestroy(() => {
    if (pubnubListener) {
      pubnubUnsubscribe()
      pubnub?.removeListener(pubnubListener)
    }
  })

  function getString(value: unknown): string {
    return typeof value === 'string' ? value : ''
  }

  function getNumber(value: unknown): number {
    if (typeof value === 'number') return value

    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  function initializePubNub() {
    const subscribeKey = $topupPaymentGatewayResult?.pubnubSubKey
    if (!subscribeKey) return

    pubnub = new PubNub({
      subscribeKey,
      userId: 'loca',
      ssl: true,
    })

    pubnubListener = {
      status: event => {
        if (event.category === 'PNNetworkUpCategory') {
          pubnubSubscribe()
        }
      },
      message: event => {
        const rawMessage = event.message

        try {
          const parsed = (typeof rawMessage === 'string'
            ? JSON.parse(rawMessage)
            : rawMessage) as PaymentMessage
          const channelText = event.channel ?? event.actualChannel ?? event.subscribedChannel ?? ''
          const channelBill = channelText
            .replace('loca-driver-center-', '')
            .replace('-paid', '')
          const billNumber = getString(parsed.billNumber)
            || getString(parsed.bill_number)
            || getString(parsed.iid)
            || channelBill

          if (!billNumber || billNumber !== $topupPaymentGatewayResult?.billNumber) {
            return
          }

          pubnub?.unsubscribeAll()
          $topupResult = {
            paidAt: getString(parsed.txnDateTime)
              || getString(parsed.txn_date_time)
              || getString(parsed.txtime)
              || getString(parsed.paid_at),
            paymentRef: getString(parsed.refNo)
              || getString(parsed.ref_no)
              || getString(parsed.payment_ref)
              || getString(parsed.fccref),
            amount: getNumber(parsed.txnAmount ?? parsed.txn_amount ?? parsed.amount),
          }

          PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageTopupSuccess })
        } catch (error) {
          console.error('WECHAT_PAYMENT_MESSAGE_PARSE_ERROR', error)
        }
      },
    }

    pubnub.addListener(pubnubListener)
  }

  function pubnubSubscribe() {
    if (!pubnub || pubnubChannels.length === 0) return

    pubnubUnsubscribe()
    pubnub.subscribe({ channels: pubnubChannels })
  }

  function pubnubUnsubscribe() {
    if (!pubnub || pubnubChannels.length === 0) return

    pubnub.unsubscribe({ channels: pubnubChannels })
  }

  async function confirmPayment() {
    try {
      $isLoading = true

      const { data } = await axiosInstance.post('/webapp/driver/topup/check-payment', {
        'bill_number': $topupPaymentGatewayResult?.billNumber,
      })

      pubnub?.unsubscribeAll()
      $topupResult = {
        paidAt: data['paid_at'],
        paymentRef: data['payment_ref'],
        amount: data['amount'],
      }

      PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageTopupSuccess })
    } catch (error) {
      console.error('DRIVER_CONFIRM_WECHAT_PAYMENT_ERROR', error)

      if (axios.isAxiosError(error)) {
        if (error.response) {
          showErrorMessage(error.response.data.message)
        } else if (error.request) {
          showErrorMessage(i18n($lang, ['Cannot connect to server, please try again', 'ບໍ່ສາມາດເຊື່ອມຕໍ່ລະບົບ ກະລຸນາລອງໃໝ່']))
        } else {
          showErrorMessage(i18n($lang, ['Please try again', 'ກະລຸນາລອງໃໝ່']))
        }
      } else {
        showErrorMessage(i18n($lang, ['Please try again', 'ກະລຸນາລອງໃໝ່']))
      }
    } finally {
      $isLoading = false
    }
  }
</script>

<main class="flex-1 overflow-y-scroll px-4">
  <img
    src="{new URL('@/assets/general/wechat-pay-logo.svg', import.meta.url).href}"
    width="180"
    class="mx-auto mb-8"
    alt="WeChat Pay"
  >

  <div class="space-y-4">
    <div class="text-xl text-center font-semibold">ສະແກນເພື່ອຊຳລະ</div>
    <div class="w-max mx-auto bg-white p-4 rounded-lg shadow">
      <QRCode value={$topupPaymentGatewayResult?.qr} size={240}/>
    </div>
    <div class="text-center">
      <div class="text-sm font-light">ເລກອ້າງອີງ</div>
      <div>{$topupPaymentGatewayResult?.billNumber}</div>
    </div>
  </div>
</main>

<div class="rounded-lg border border-[#E80000] bg-[#F8F8F8] p-4 m-8 text-[#E80000] text-center text-2xl font-bold">
  LAK {$topupPaymentGatewayResult?.amount?.toLocaleString()}
</div>

<button
  type="button"
  class="h-14 bg-[#1D3549] mt-auto flex justify-center items-center text-white text-xl font-semibold"
  on:click={confirmPayment}
>
  ກວດສອບ
</button>
